/**
 * WebSocket Vuex 模块（对齐小程序 shopro/store/modules/websocket.js）
 */
import WebSocketManager from '@/utils/websocket'
import { WEBSOCKET_URL } from '@/config'
import {
  buildLoginMessage,
  shouldSendGlobalLogin,
  resetWsMarksOnReconnect
} from '@/utils/wsProtocol'
import { playTipSound } from '@/utils/tipSound'

const state = {
  websocket: null,
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  lastError: null,
  sessionReady: false
}

const mutations = {
  SET_WEBSOCKET(state, ws) {
    state.websocket = ws
  },
  SET_STATUS(state, payload = {}) {
    Object.keys(payload).forEach(key => {
      if (key in state) state[key] = payload[key]
    })
  },
  SET_ERROR(state, err) {
    state.lastError = err
  },
  SET_SESSION_READY(state, ready) {
    state.sessionReady = !!ready
  }
}

const actions = {
  async initWebSocket({ state, commit, dispatch }, options = {}) {
    if (state.websocket) {
      const status = state.websocket.getStatus()
      if (status.isConnected || status.isConnecting) return state.websocket
    }

    const manager = new WebSocketManager({
      url: WEBSOCKET_URL,
      heartbeatInterval: 30000,
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      autoReconnect: true,
      ...options
    })

    manager.on('open', () => {
      commit('SET_STATUS', {
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        reconnectAttempts: 0
      })
    })

    manager.on('close', () => {
      commit('SET_STATUS', {
        isConnected: false,
        isConnecting: false
      })
      commit('SET_SESSION_READY', false)
    })

    manager.on('error', err => {
      commit('SET_ERROR', err)
    })

    manager.on('reconnect', payload => {
      resetWsMarksOnReconnect()
      commit('SET_STATUS', {
        isReconnecting: false,
        reconnectAttempts: (payload && payload.attempts) || state.reconnectAttempts
      })
      commit('SET_SESSION_READY', false)
    })

    manager.on('message', data => {
      dispatch('handleRawMessage', data)
    })

    commit('SET_WEBSOCKET', manager)
    commit('SET_STATUS', { isConnecting: true })
    await manager.connect()
    return manager
  },

  handleRawMessage({ dispatch, commit, rootGetters }, raw) {
    let data = raw
    // new_message 可能无嵌套 data，字段直接挂在根上
    if (raw && (raw.type === 'new_message' || raw.type === 'message') && raw.data) {
      data = raw.data
    }
    if (!data || typeof data !== 'object') return

    if (data.type === 'login_success') {
      dispatch('chat/onWsLoginSuccess', data, { root: true })
      return
    }

    if (data.type === 'error') {
      commit('SET_ERROR', data.msg || data.message || 'WebSocket 错误')
      dispatch('chat/onWsError', data, { root: true })
      return
    }

    if (data.type === 'ping' || data.type === 'pong' || data.type === 'login' || data.type === 'read') {
      return
    }

    // 统一服务端推送字段（content/msg_type/creatime/message_id）
    const normalized = {
      ...data,
      id: data.id != null ? data.id : data.message_id,
      message:
        data.message != null
          ? data.message
          : data.content != null
            ? data.content
            : '',
      message_type: data.message_type || data.msg_type || 'text',
      createtime: data.createtime || data.creatime || data.create_time,
      sender_id: data.sender_id != null ? data.sender_id : data.from_user_id,
      tax_chat_pool_id: data.tax_chat_pool_id,
      session_id: data.session_id
    }

    const looksLikeChat =
      normalized.type === 'new_message' ||
      normalized.type === 'chat' ||
      normalized.message_type ||
      normalized.message != null ||
      normalized.content != null ||
      normalized.sender_id != null ||
      normalized.from_user_id != null

    if (looksLikeChat) {
      // 非自己发送的消息播放提示音（每次只播一遍）
      const userInfo = rootGetters.userInfo || {}
      const myId = userInfo.id || userInfo.user_id
      const senderId = normalized.sender_id
      const isSelf =
        myId != null &&
        senderId != null &&
        String(myId) === String(senderId)
      if (!isSelf) {
        playTipSound()
      }
      dispatch('chat/onWsChatMessage', normalized, { root: true })
    }
  },

  async connectAppWebSocket({ dispatch, state, rootGetters, commit }, options = {}) {
    const token = rootGetters.token
    const userInfo = rootGetters.userInfo
    if (!token || !userInfo) return false

    const loginPayload = buildLoginMessage(options)
    const sendLogin = () => {
      if (!shouldSendGlobalLogin(loginPayload)) return
      dispatch('sendMessage', loginPayload)
    }

    const ws = state.websocket
    const live = !!(ws && (ws.isConnected || ws.isConnecting))
    if (ws && live) {
      if (ws.isConnected) sendLogin()
      return true
    }

    await dispatch('initWebSocket', options)
    const created = state.websocket
    if (!created) return false

    created.on('open', sendLogin)
    created.on('reconnect', () => {
      resetWsMarksOnReconnect()
      commit('SET_SESSION_READY', false)
      sendLogin()
    })

    if (created.isConnected) sendLogin()
    return true
  },

  sendMessage({ state }, payload) {
    if (!state.websocket) {
      return Promise.reject(new Error('WebSocket 未初始化'))
    }
    return state.websocket.send(payload)
  },

  closeWebSocket({ state, commit }) {
    if (state.websocket) {
      state.websocket.close()
    }
    commit('SET_WEBSOCKET', null)
    commit('SET_STATUS', {
      isConnected: false,
      isConnecting: false,
      isReconnecting: false,
      reconnectAttempts: 0
    })
    commit('SET_SESSION_READY', false)
    resetWsMarksOnReconnect()
  },

  markSessionReady({ commit }, ready = true) {
    commit('SET_SESSION_READY', ready)
  }
}

const getters = {
  wsConnected: state => state.isConnected,
  wsSessionReady: state => state.sessionReady,
  wsStatus: state => ({
    isConnected: state.isConnected,
    isConnecting: state.isConnecting,
    isReconnecting: state.isReconnecting,
    reconnectAttempts: state.reconnectAttempts,
    sessionReady: state.sessionReady,
    lastError: state.lastError
  })
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
